import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, CheckCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Slider } from '../../components/ui/Slider';
import { Progress } from '../../components/ui/Progress';
import { ScrollArea } from '../../components/ui/ScrollArea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import { podcastEpisodes, podcastCollections } from '../../src/seed';
export function PodcastEpisodeDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);
  const episode = podcastEpisodes.find(e => e.id === id);
  if (!episode) {
    return <Card data-id="element-436">
        <CardHeader data-id="element-437">
          <CardTitle data-id="element-438">Episode not found</CardTitle>
        </CardHeader>
      </Card>;
  }
  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const episodeCollections = podcastCollections.filter(c => episode.collectionIds.includes(c.id));
  return <div className="space-y-6 pb-10" data-id="element-439">
      <div data-id="element-440">
        <Button variant="ghost" size="sm" className="-ml-3 mb-2 text-muted-foreground" onClick={() => navigate(-1)} data-id="element-441">
          <ArrowLeft className="mr-2 h-4 w-4" data-id="element-442" />
          Back
        </Button>
        <h1 className="text-3xl font-bold tracking-tight mb-3" data-id="element-443">
          {episode.title}
        </h1>
        <div className="flex flex-wrap gap-2 mb-3" data-id="element-444">
          {episodeCollections.map(c => <Badge key={c.id} variant="outline" data-id="element-445">
              {c.name}
            </Badge>)}
        </div>
        <p className="text-muted-foreground text-sm" data-id="element-446">
          {episode.show} · Episode {episode.id.replace('pe', '')} ·{' '}
          {Math.floor(episode.duration / 60)} min ·{' '}
          {formatDate(episode.published)}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-447">
        <div className="lg:col-span-2 space-y-6" data-id="element-448">
          <Card data-id="element-449">
            <CardContent className="p-6 space-y-6" data-id="element-450">
              <div className="h-24 bg-muted rounded-md flex items-center justify-center text-muted-foreground relative overflow-hidden" data-id="element-451">
                <div className="absolute inset-0 flex items-center px-2" data-id="element-452">
                  {Array.from({
                  length: 60
                }).map((_, i) => <div key={i} className="flex-1 mx-[1px] bg-primary/40 rounded-full" style={{
                  height: `${Math.max(10, Math.random() * 100)}%`
                }} data-id="element-453" />)}
                </div>
              </div>

              <div className="flex items-center gap-4" data-id="element-454">
                <Button size="icon" className="rounded-full h-12 w-12 shrink-0" onClick={() => setIsPlaying(!isPlaying)} data-id="element-455">
                  {isPlaying ? <Pause className="h-5 w-5" data-id="element-456" /> : <Play className="h-5 w-5 ml-1" data-id="element-457" />}
                </Button>
                <div className="flex-1 space-y-2" data-id="element-458">
                  <Progress value={isPlaying ? 35 : 0} className="h-2" data-id="element-459" />
                  <div className="flex justify-between text-xs text-muted-foreground font-medium" data-id="element-460">
                    <span data-id="element-461">{isPlaying ? '03:42' : '0:00'}</span>
                    <span data-id="element-462">{formatDuration(episode.duration)}</span>
                  </div>
                </div>
                <div className="w-24 shrink-0" data-id="element-463">
                  <Slider defaultValue={[80]} max={100} step={1} data-id="element-464" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-id="element-465">
            <CardContent className="p-6" data-id="element-466">
              <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm" data-id="element-467">
                <div data-id="element-468">
                  <p className="text-muted-foreground mb-1" data-id="element-469">Duration</p>
                  <p className="font-medium" data-id="element-470">
                    {formatDuration(episode.duration)}
                  </p>
                </div>
                <div data-id="element-471">
                  <p className="text-muted-foreground mb-1" data-id="element-472">Published</p>
                  <p className="font-medium" data-id="element-473">{formatDate(episode.published)}</p>
                </div>
                <div data-id="element-474">
                  <p className="text-muted-foreground mb-1" data-id="element-475">File size</p>
                  <p className="font-medium" data-id="element-476">48.2 MB</p>
                </div>
                <div data-id="element-477">
                  <p className="text-muted-foreground mb-1" data-id="element-478">Format</p>
                  <p className="font-medium" data-id="element-479">MP3 · 128 kbps</p>
                </div>
                <div data-id="element-480">
                  <p className="text-muted-foreground mb-1" data-id="element-481">Host</p>
                  <p className="font-medium" data-id="element-482">Dr. Sarah Chen</p>
                </div>
                <div data-id="element-483">
                  <p className="text-muted-foreground mb-1" data-id="element-484">Guest</p>
                  <p className="font-medium" data-id="element-485">Dr. James Rivera</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6" data-id="element-486">
          <Card data-id="element-487">
            <CardHeader data-id="element-488">
              <CardTitle data-id="element-489">Transcript</CardTitle>
            </CardHeader>
            <CardContent data-id="element-490">
              <ScrollArea className="h-[500px] pr-4" data-id="element-491">
                <div className="space-y-2" data-id="element-492">
                  <div className="bg-muted rounded-md p-3 flex" data-id="element-493">
                    <span className="text-xs text-muted-foreground font-mono w-16 shrink-0" data-id="element-494">
                      00:00:12
                    </span>
                    <p className="text-sm" data-id="element-495">
                      Welcome back to Clinical Rounds. Today we are tackling one
                      of the most common presentations in the emergency
                      department — acute chest pain.
                    </p>
                  </div>
                  <div className="bg-muted rounded-md p-3 flex" data-id="element-496">
                    <span className="text-xs text-muted-foreground font-mono w-16 shrink-0" data-id="element-497">
                      00:01:28
                    </span>
                    <p className="text-sm" data-id="element-498">
                      The differential for chest pain is broad, but our approach
                      should be systematic. We always start with the life
                      threats: ACS, PE, aortic dissection, tension pneumothorax,
                      and cardiac tamponade.
                    </p>
                  </div>
                  <div className="bg-accent text-accent-foreground rounded-md p-3 flex" data-id="element-499">
                    <span className="text-xs text-accent-foreground/70 font-mono w-16 shrink-0" data-id="element-500">
                      00:03:42
                    </span>
                    <p className="text-sm" data-id="element-501">
                      The standard workup for acute chest pain in the ED begins
                      with a focused history. Ask about onset, character,
                      radiation, and associated symptoms. A 12-lead ECG should
                      be obtained within ten minutes of arrival.
                    </p>
                  </div>
                  <div className="bg-muted rounded-md p-3 flex" data-id="element-502">
                    <span className="text-xs text-muted-foreground font-mono w-16 shrink-0" data-id="element-503">
                      00:06:15
                    </span>
                    <p className="text-sm" data-id="element-504">
                      Troponin is our biomarker of choice. High-sensitivity
                      troponin assays allow us to rule out MI much faster than
                      conventional assays — often within one to three hours
                      using serial measurements.
                    </p>
                  </div>
                  <div className="bg-muted rounded-md p-3 flex" data-id="element-505">
                    <span className="text-xs text-muted-foreground font-mono w-16 shrink-0" data-id="element-506">
                      00:09:30
                    </span>
                    <p className="text-sm" data-id="element-507">
                      Lets talk about the HEART score. It integrates History,
                      ECG, Age, Risk factors, and Troponin into a single risk
                      stratification tool that guides disposition decisions.
                    </p>
                  </div>
                  <div className="bg-muted rounded-md p-3 flex" data-id="element-508">
                    <span className="text-xs text-muted-foreground font-mono w-16 shrink-0" data-id="element-509">
                      00:12:48
                    </span>
                    <p className="text-sm" data-id="element-510">
                      For intermediate-risk patients, CT coronary angiography
                      has emerged as a powerful tool. It has excellent negative
                      predictive value and can safely identify patients for
                      early discharge.
                    </p>
                  </div>
                  <div className="bg-muted rounded-md p-3 flex" data-id="element-511">
                    <span className="text-xs text-muted-foreground font-mono w-16 shrink-0" data-id="element-512">
                      00:16:05
                    </span>
                    <p className="text-sm" data-id="element-513">
                      One pitfall I see frequently is anchoring on the initial
                      ECG. Remember, a single normal ECG does not rule out ACS.
                      Serial ECGs are essential, especially if symptoms persist
                      or recur.
                    </p>
                  </div>
                  <div className="bg-muted rounded-md p-3 flex" data-id="element-514">
                    <span className="text-xs text-muted-foreground font-mono w-16 shrink-0" data-id="element-515">
                      00:19:22
                    </span>
                    <p className="text-sm" data-id="element-516">
                      To summarise todays key points: use a systematic approach,
                      get that ECG early, leverage high-sensitivity troponin
                      protocols, and never anchor on a single data point. Thanks
                      for listening.
                    </p>
                  </div>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      <Card className="w-full" data-id="element-517">
        <CardHeader data-id="element-518">
          <CardTitle data-id="element-519">Tools</CardTitle>
        </CardHeader>
        <CardContent data-id="element-520">
          <Tabs defaultValue="summarise" data-id="element-521">
            <TabsList data-id="element-522">
              <TabsTrigger value="transcribe" data-id="element-523">Transcribe</TabsTrigger>
              <TabsTrigger value="summarise" data-id="element-524">Summarise</TabsTrigger>
              <TabsTrigger value="publish" data-id="element-525">Publish</TabsTrigger>
            </TabsList>
            <div className="mt-4" data-id="element-526">
              <TabsContent value="transcribe" data-id="element-527">
                <div className="flex items-center gap-3" data-id="element-528">
                  <CheckCircle className="h-5 w-5 text-green-500" data-id="element-529" />
                  <div data-id="element-530">
                    <p className="font-medium" data-id="element-531">Transcribed 2 hours ago</p>
                    <p className="text-sm text-muted-foreground" data-id="element-532">
                      58 min · 12,000 words
                    </p>
                  </div>
                </div>
                <Button variant="ghost" className="mt-3" data-id="element-533">
                  Re-transcribe
                </Button>
              </TabsContent>
              <TabsContent value="summarise" data-id="element-534">
                <div className="bg-muted rounded-md p-4 text-sm leading-relaxed" data-id="element-535">
                  In this episode of Clinical Rounds Podcast, Dr. Sarah Chen and
                  guest Dr. James Rivera discuss a systematic approach to
                  evaluating acute chest pain in the emergency department. The
                  conversation begins with the critical importance of rapidly
                  identifying life-threatening causes including acute coronary
                  syndrome, pulmonary embolism, aortic dissection, tension
                  pneumothorax, and cardiac tamponade. Dr. Rivera emphasizes
                  that a 12-lead ECG should be obtained within ten minutes of
                  patient arrival and highlights the limitations of relying on a
                  single normal ECG to exclude ACS. The discussion covers the
                  role of high-sensitivity troponin assays in accelerating
                  rule-out protocols, often enabling confident decisions within
                  one to three hours. The HEART score is presented as a
                  practical risk stratification tool that integrates clinical
                  history, ECG findings, age, risk factors, and troponin levels
                  to guide disposition. For intermediate-risk patients, CT
                  coronary angiography is discussed as an effective imaging
                  modality with excellent negative predictive value. Key
                  clinical pearls include avoiding anchoring bias, obtaining
                  serial ECGs when symptoms persist, and maintaining a broad
                  differential even when initial results appear reassuring. The
                  episode concludes with a concise summary of the systematic
                  approach recommended for emergency physicians evaluating
                  undifferentiated chest pain.
                </div>
                <div className="flex gap-3 mt-4" data-id="element-536">
                  <Button variant="outline" data-id="element-537">Regenerate</Button>
                  <Button data-id="element-538">Accept</Button>
                </div>
              </TabsContent>
              <TabsContent value="publish" data-id="element-539">
                <div className="space-y-3" data-id="element-540">
                  <Button data-id="element-541">Publish to website</Button>
                  <p className="text-sm text-muted-foreground" data-id="element-542">
                    This will publish the episode summary and transcript to the
                    TMA website.
                  </p>
                  <p className="text-xs text-muted-foreground" data-id="element-543">
                    Last published: never
                  </p>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </CardContent>
      </Card>
    </div>;
}